import type { IAppBannerImage } from '@/configs/interfaces/IAppBannerImage';
import { TMemberClass } from '@/models/Auth/interfaces/IAuthValidatePayload';
import { IMemberAgreeAgreements } from '@/models/Auth/interfaces/IAuthValidateSerialized';
import CartActions from '@/models/Cart/CartActions';
import CartRequestBuilder from '@/models/Cart/CartRequestBuilder';
import { TJoyJoinType } from '@/models/Member/interfaces/TMember';
import { EN_APP_DEVICE } from '@/utils/functions/device';
import { getYaNative } from '@yanolja-org/ya-native';
import * as Cookies from 'js-cookie';
import { action, computed, observable } from 'mobx';
import * as R from 'ramda';
import { createContext } from 'react';

import { ToastStore } from './partial/ToastStore';

export const COOKIE_SMART_BANNER = 'top-smart-banner';

export interface ISmartBanner {
  path?: string;
  params?: Record<string, string | number | undefined>;
  target?: string;
  version?: 'V2' | 'V3';
  image?: IAppBannerImage;
}

export interface IGlobalStoreProps {
  appDevice: EN_APP_DEVICE | null;
  device: EN_APP_DEVICE | null;
  referrer: string;
  cartCount: number;
  cartLink: string;
  hasSmartBannerCookie: boolean;
  trackcode: string | undefined;
  isKakaoChannel: boolean;
  isKakaoEmbedded: boolean;
  isNaverHotelEvent: boolean;
  memberType?: TJoyJoinType;
  browserName?: string;
  agreements?: IMemberAgreeAgreements;
  memberClass?: TMemberClass;
  isLoggedIn: boolean;
  requestedUrl: string;
}

export default class GlobalStore {
  @observable
  smartBannerInitialized: boolean = false;
  @observable
  isOpenCartNotAvailblePopup: boolean = false;

  /*
    appDevice = 웹뷰를 호출한 디바이스 모델
    ex) appDevice === EN_APP_DEVICE.IOS => ios 에서 띄워진 웹뷰

    device = 모바일 웹을 호출한 디바이스 모델
    ex) device === EN_APP_DEVICE.IOS => ios 에서 띄워진 모웹
  */
  @observable
  private appDevice: EN_APP_DEVICE | null;
  @observable
  private device: EN_APP_DEVICE | null;
  @observable
  private smartBanner: ISmartBanner = {};
  @observable
  hasSmartBannerCookie: boolean;

  @observable
  private cartCount: number = 0;

  readonly toast = new ToastStore();
  readonly memberType?: TJoyJoinType;
  readonly isKakaoChannel: boolean;
  readonly isKakaoEmbedded: boolean;
  readonly browserName?: string;

  private referrer: string = '';
  private cartLink: string = '';
  private trackcode?: string = '';
  private isNaverHotelEvent: boolean;
  private agreements?: IMemberAgreeAgreements;
  private memberClass?: TMemberClass;
  private isLoggedIn: boolean = false;
  private requestedUrl: string = '';

  private readonly cartActions = new CartActions(CartRequestBuilder);

  constructor({
    appDevice,
    device,
    referrer,
    cartCount,
    cartLink,
    hasSmartBannerCookie,
    trackcode,
    isNaverHotelEvent,
    memberType,
    isKakaoChannel,
    isKakaoEmbedded,
    browserName,
    agreements,
    memberClass,
    isLoggedIn,
    requestedUrl,
  }: IGlobalStoreProps) {
    this.appDevice = appDevice;
    this.device = device;
    this.referrer = referrer;
    this.cartCount = cartCount;
    this.cartLink = cartLink;
    this.hasSmartBannerCookie = hasSmartBannerCookie;
    this.trackcode = trackcode;
    this.isNaverHotelEvent = isNaverHotelEvent;
    this.memberType = memberType;
    this.isKakaoChannel = isKakaoChannel;
    this.isKakaoEmbedded = isKakaoEmbedded;
    this.browserName = browserName;
    this.agreements = agreements;
    this.memberClass = memberClass;
    this.isLoggedIn = isLoggedIn;
    this.requestedUrl = requestedUrl;
  }

  @computed
  get AppDevice(): EN_APP_DEVICE | null {
    return this.appDevice;
  }

  set AppDevice(appDevice: EN_APP_DEVICE | null) {
    this.appDevice = appDevice;
  }

  @computed
  get Device(): EN_APP_DEVICE | null {
    return this.device;
  }

  set Device(device: EN_APP_DEVICE | null) {
    this.device = device;
  }

  @computed
  get IsPc(): boolean {
    if (this.device) {
      return false;
    }
    return true;
  }

  @computed
  get SmartBanner(): ISmartBanner | null {
    return this.smartBanner;
  }

  set SmartBanner(smartBanner: ISmartBanner | null) {
    this.smartBanner = smartBanner || {};
  }

  @computed
  get SmartBannerClosed(): boolean {
    return this.hasSmartBannerCookie;
  }

  @computed
  get ShowSmartBanner(): boolean {
    // [YWEB-3662] 트립어드바이저 앱 웹뷰를 위해 스마트배너 미노출 처리
    // [FES-109] 네이버호텔을 통해서 진입시 스마트배너 미노출 처리
    if (
      this.IsPc ||
      this.SmartBannerClosed ||
      this.trackcode === 'tripadvisor' ||
      this.IsFromNaverHotel ||
      this.IsFromHotelsCombined
    ) {
      return false;
    }
    return !R.isEmpty(this.SmartBanner);
  }

  @computed
  get Referrer(): string {
    return this.referrer;
  }

  @computed
  get CartCount(): number {
    return this.cartCount;
  }

  set CartCount(cartCount: number) {
    this.cartCount = cartCount;
  }

  @action.bound
  async updateCartCount(): Promise<void> {
    const cartReqParams = {
      headers: {
        cognitoId: '',
        ywt: '',
      },
    };
    const cartCountEntity = await this.cartActions.cartCount(cartReqParams);
    this.CartCount = cartCountEntity.map(R.prop('data')).caseOf({
      left: () => 0,
      right: (r) => r.data?.count ?? 0,
    });
  }

  @computed
  get IsFromNaverHotel(): boolean {
    return this.trackcode?.startsWith('naverhotel') || false;
  }

  @computed
  get IsNaverHotelEvent(): boolean {
    return this.isNaverHotelEvent;
  }

  @computed
  get IsFromHotelsCombined(): boolean {
    return (this.trackcode?.startsWith('HC') && this.trackcode !== 'HC_ETC') || false;
  }

  get TrackCode(): string | undefined {
    return this.trackcode;
  }

  /**
   * 장바구니를 사용 못하는 조건을 체크하기 위해 추가
   */
  @computed
  get IsCartAvailable(): boolean {
    return true;
  }

  get CartLink(): string {
    return this.cartLink;
  }

  @computed
  get IsOpenCartNotAvailablePopup(): boolean {
    return this.isOpenCartNotAvailblePopup;
  }

  get IsLoggedIn(): boolean {
    return this.isLoggedIn;
  }

  get RequestedUrl(): string {
    return this.requestedUrl;
  }

  @action.bound
  closeSmartBanner(): void {
    Cookies.set(COOKIE_SMART_BANNER, 'Y', { expires: 1, domain: '.yanolja.com' });
    this.SmartBanner = {};
    this.hasSmartBannerCookie = true;
  }

  @action.bound
  openCartNotAvailablePopup(): void {
    this.isOpenCartNotAvailblePopup = true;
  }

  @action.bound
  closeCartNotAvailablePopup(): void {
    this.isOpenCartNotAvailblePopup = false;
  }

  /**
   * 네비게이션 장바구니 버튼 click
   * 네이버 호텔을 통해 진입 시 장바구니 진입을 막음
   */
  @action.bound
  onClickNavCartBtn(): void {
    const yaNative = getYaNative();

    if (yaNative.isEnabled) {
      yaNative.wv({
        url: this.cartLink,
        method: 'get',
      });
      return;
    }

    location.href = this.cartLink;
  }

  @computed
  get IsIOS(): boolean {
    return this.AppDevice === EN_APP_DEVICE.IOS || this.Device === EN_APP_DEVICE.IOS;
  }

  @computed
  get IsAndroid(): boolean {
    return this.AppDevice === EN_APP_DEVICE.ANDROID || this.Device === EN_APP_DEVICE.ANDROID;
  }

  @computed
  get Agreements(): IMemberAgreeAgreements | undefined {
    return this.agreements;
  }

  @computed
  get MemberClass(): TMemberClass | undefined {
    return this.memberClass;
  }
}

export const GlobalStoreCtx = createContext<GlobalStore>(null!);
